import { useMemo, useCallback } from 'react';
import type { SplitType } from '@sharecost/shared';
import { FieldError } from '@/components/common/FormHelpers';
import { useTranslation } from 'react-i18next';

interface GroupMember {
  user_id: string;
  user?: { display_name: string } | null;
}

interface SplitMembersSectionProps {
  members: GroupMember[];
  currentUserId?: string;
  splitType: SplitType;
  splitWith: string[];
  onSplitWithChange: (ids: string[]) => void;
  totalAmount: number;
  exactAmounts: Record<string, string>;
  onExactAmountsChange: (amounts: Record<string, string>) => void;
  percentages: Record<string, string>;
  onPercentagesChange: (pcts: Record<string, string>) => void;
  shares: Record<string, string>;
  onSharesChange: (shares: Record<string, string>) => void;
  showErrors?: boolean;
  splitError?: string;
  splitWithError?: string;
}

export function SplitMembersSection({
  members,
  currentUserId,
  splitType,
  splitWith,
  onSplitWithChange,
  totalAmount,
  exactAmounts,
  onExactAmountsChange,
  percentages,
  onPercentagesChange,
  shares,
  onSharesChange,
  showErrors,
  splitError,
  splitWithError,
}: SplitMembersSectionProps) {
  const { t } = useTranslation();
  const toggleMember = (userId: string) => {
    onSplitWithChange(
      splitWith.includes(userId)
        ? splitWith.filter(id => id !== userId)
        : [...splitWith, userId],
    );
  };

  const splitSummary = useMemo(() => {
    if (splitType === 'exact' && splitWith.length > 0 && totalAmount > 0) {
      const sum = splitWith.reduce((s, id) => s + (parseFloat(exactAmounts[id]) || 0), 0);
      const diff = totalAmount - sum;
      const isBalanced = Math.abs(diff) <= 0.01;
      return { sum, target: totalAmount, diff, isBalanced, decimals: 2, unit: '' };
    }
    if (splitType === 'percentage' && splitWith.length > 0) {
      const sum = splitWith.reduce((s, id) => s + (parseFloat(percentages[id]) || 0), 0);
      const diff = 100 - sum;
      const isBalanced = Math.abs(diff) <= 0.1;
      return { sum, target: 100, diff, isBalanced, decimals: 1, unit: '%' };
    }
    return null;
  }, [splitType, splitWith, exactAmounts, percentages, totalAmount]);

  const handleBlurAutoFill = useCallback(() => {
    if (splitType === 'exact' && totalAmount > 0) {
      const emptyMembers = splitWith.filter(id => {
        const val = exactAmounts[id];
        return !val || val.trim() === '' || parseFloat(val) === 0;
      });
      if (emptyMembers.length === 1) {
        const filledSum = splitWith
          .filter(id => id !== emptyMembers[0])
          .reduce((s, id) => s + (parseFloat(exactAmounts[id]) || 0), 0);
        const remaining = totalAmount - filledSum;
        if (remaining > 0) {
          onExactAmountsChange({
            ...exactAmounts,
            [emptyMembers[0]]: remaining.toFixed(2),
          });
        }
      }
    }
    if (splitType === 'percentage') {
      const emptyMembers = splitWith.filter(id => {
        const val = percentages[id];
        return !val || val.trim() === '' || parseFloat(val) === 0;
      });
      if (emptyMembers.length === 1) {
        const filledSum = splitWith
          .filter(id => id !== emptyMembers[0])
          .reduce((s, id) => s + (parseFloat(percentages[id]) || 0), 0);
        const remaining = 100 - filledSum;
        if (remaining > 0) {
          onPercentagesChange({
            ...percentages,
            [emptyMembers[0]]: remaining.toFixed(1),
          });
        }
      }
    }
  }, [splitType, splitWith, exactAmounts, percentages, totalAmount, onExactAmountsChange, onPercentagesChange]);

  return (
    <div>
      <label className="block text-sm font-medium text-text-secondary mb-2">
        {t('web:splitMembersSection.splitWith')}
      </label>
      <div className="space-y-2">
        {members.map(m => (
          <label
            key={m.user_id}
            className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-surface-hover ${
              showErrors && splitWithError ? 'border-red-400' : 'border-border-primary'
            }`}
          >
            <input
              type="checkbox"
              checked={splitWith.includes(m.user_id)}
              onChange={() => toggleMember(m.user_id)}
              className="h-4 w-4 rounded border-border-secondary text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm text-text-secondary flex-1">
              {m.user?.display_name}{m.user_id === currentUserId ? ` ${t('web:common.youSuffix')}` : ''}
            </span>

            {splitType === 'exact' && splitWith.includes(m.user_id) && (
              <input
                type="number"
                step="0.01"
                min="0"
                value={exactAmounts[m.user_id] ?? ''}
                onChange={e =>
                  onExactAmountsChange({ ...exactAmounts, [m.user_id]: e.target.value })
                }
                onBlur={handleBlurAutoFill}
                placeholder="0.00"
                className="w-24 rounded border border-border-secondary px-2 py-1 text-sm"
              />
            )}
            {splitType === 'percentage' && splitWith.includes(m.user_id) && (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={percentages[m.user_id] ?? ''}
                  onChange={e =>
                    onPercentagesChange({ ...percentages, [m.user_id]: e.target.value })
                  }
                  onBlur={handleBlurAutoFill}
                  placeholder="0"
                  className="w-20 rounded border border-border-secondary px-2 py-1 text-sm"
                />
                <span className="text-sm text-text-quaternary">%</span>
              </div>
            )}
            {splitType === 'shares' && splitWith.includes(m.user_id) && (
              <input
                type="number"
                step="1"
                min="1"
                value={shares[m.user_id] ?? '1'}
                onChange={e =>
                  onSharesChange({ ...shares, [m.user_id]: e.target.value })
                }
                className="w-20 rounded border border-border-secondary px-2 py-1 text-sm"
              />
            )}
          </label>
        ))}
      </div>

      {splitSummary && (
        <div
          className={`mt-3 flex items-center justify-between rounded-lg px-3 py-2 text-sm ${
            splitSummary.isBalanced
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
              : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
          }`}
        >
          <span>
            {t('web:splitMembersSection.sum')} {splitSummary.sum.toFixed(splitSummary.decimals)}{splitSummary.unit}
            {' / '}
            {splitSummary.target.toFixed(splitSummary.decimals)}{splitSummary.unit}
          </span>
          <span className="font-medium">
            {splitSummary.isBalanced
              ? t('expenses:split.balanced')
              : splitSummary.diff > 0
                ? t('web:splitMembersSection.remaining', { amount: `${splitSummary.diff.toFixed(splitSummary.decimals)}${splitSummary.unit}` })
                : t('web:splitMembersSection.overBy', { amount: `${Math.abs(splitSummary.diff).toFixed(splitSummary.decimals)}${splitSummary.unit}` })}
          </span>
        </div>
      )}

      {showErrors && <FieldError message={splitWithError} />}
      {showErrors && <FieldError message={splitError} />}
    </div>
  );
}
